import { axiosInstance } from '../utils/axios';
import SecureStorage from '../utils/secureStorage';

export interface GameScoreRequest {
  score: number;
  level?: number;
  gameName: string;
  playDuration?: number;
  gameData?: any;
  activityId?: string;
}

export interface GameScoreResponse {
  success: boolean;
  message: string;
  data: {
    score: number;
    level: number;
    isNewHighScore: boolean;
    gameName: string;
    totalPlays: number;
    monthlyHighScore: number;
    dailyPlaysRemaining: number;
    resetTime: string;
  };
}

export interface Game {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  imageUrl?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  maxPlayPerDay: number;
  resetTime: string;
  gamePath: string;
  gameConfig: any;
  isCompetitive: boolean;
  prizeDescription?: string;
  livesPrice: number; // Price of one purchase unit
  livesPerUnit: number; // Number of lives granted per purchase unit
  livesCurrency: string; // ISO 4217 currency code
  allowLivesPurchase: boolean;
  createdAt: string;
  updatedAt: string;
  // Backward compatibility for older API responses
  livesPerDollar?: number;
}

// Helper to normalize legacy field naming just in case backend not yet deployed everywhere
export const normalizeGamePricing = (game: Game): Game => {
  if ((game as any).livesPerDollar && !game.livesPerUnit) {
    return { ...game, livesPerUnit: (game as any).livesPerDollar };
  }
  return game;
};

export interface GameStats {
  totalPlays: number;
  totalScore: number;
  averageScore: number;
  highScores: number;
  recentGames: Array<{
    gameName: string;
    score: number;
    level: number;
    playDate: string;
    isHighScore: boolean;
  }>;
  // When querying stats for a specific game the backend may include these optional fields
  dailyPlaysRemaining?: number;
  purchasedLives?: number;
  totalDailyLimit?: number;
}

const withBearer = (token: string) => token?.startsWith('Bearer ') ? token : `Bearer ${token}`;

// Submit game score (requires authentication)
export const submitGameScore = async (gameData: GameScoreRequest): Promise<GameScoreResponse> => {
  console.log('🎯 submitGameScore called with:', gameData);
  
  if (!gameData || typeof gameData.score !== 'number' || gameData.score < 0) {
    // Allow zero scores (0) to be submitted so plays are recorded even when player scores nothing
    console.log('❌ Invalid score payload:', gameData);
    throw new Error('Invalid score payload');
  }
  
  const rawToken = SecureStorage.getCustomerToken();
  console.log('🔑 Raw token exists:', !!rawToken);
  console.log('🔑 Raw token preview:', rawToken ? rawToken.substring(0, 50) + '...' : 'null');
  
  if (!rawToken) {
    console.log('❌ No token found');
    throw new Error('Customer authentication required');
  }

  // Clean token - remove existing Bearer prefix if present
  const cleanToken = rawToken.replace(/^Bearer\s+/i, '');
  const bearerToken = `Bearer ${cleanToken}`;
  console.log('🔑 Clean token preview:', cleanToken.substring(0, 50) + '...');
  console.log('🔑 Bearer token preview:', bearerToken.substring(0, 50) + '...');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: bearerToken
  };

  console.log('📤 Making API call to /public/games/score');
  console.log('📤 Headers:', { ...headers, Authorization: bearerToken.substring(0, 30) + '...' });
  
  try {
    const response = await axiosInstance.post('/public/games/score', gameData, { headers });
    console.log('✅ Score submitted successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ API call failed:', error);
    console.error('❌ Response data:', error?.response?.data);
    console.error('❌ Status:', error?.response?.status);
    throw error;
  }
};

// Get all active games
export const getGames = async (): Promise<{ success: boolean; data: Game[] }> => {
  try {
    const response = await axiosInstance.get('/public/games/games');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching games:', error);
    throw error;
  }
};

// Get game leaderboard
export const getLeaderboard = async (gameName: string, period: 'monthly' | 'weekly' = 'monthly'): Promise<any> => {
  try {
    const res = await fetch(`/public/games/leaderboard?gameName=${encodeURIComponent(gameName)}&period=${period}`, { 
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to load leaderboard: ${res.status}`);
    return await res.json();
  } catch (error: any) {
    throw error;
  }
};

// Get customer game statistics
export const getCustomerGameStats = async (gameName?: string): Promise<{ success: boolean; data: GameStats }> => {
  const rawToken = SecureStorage.getCustomerToken();
  if (!rawToken) {
    throw new Error('Customer not authenticated - please sign in again');
  }
  
  try {
    const url = gameName 
      ? `/public/games/customer-stats?gameName=${gameName}`
      : '/public/games/customer-stats';
    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: withBearer(rawToken)
      }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      // Do not clear token or redirect for games
      throw new Error('Unauthorized for games');
    }
    throw error;
  }
};

// Purchase extra lives for a game
export interface PurchaseLivesRequest {
  gameId: string;
  livesCount: number;
  paymentIntentId?: string;
}

export interface PurchaseLivesResponse {
  success: boolean;
  message: string;
  data: {
    livesCount: number;
    amountPaid: number;
    newDailyLimit: number;
    expiresAt: string;
    gameDisplayName: string;
  };
}

export const purchaseLives = async (request: PurchaseLivesRequest): Promise<PurchaseLivesResponse> => {
  const rawToken = SecureStorage.getCustomerToken();
  if (!rawToken) {
    throw new Error('Customer not authenticated - please sign in again');
  }
  
  try {
    const response = await axiosInstance.post('/public/games/purchase-lives', request, {
      headers: {
        Authorization: withBearer(rawToken),
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error purchasing lives:', error);
    throw error;
  }
};

// Register a play immediately when starting a game (decrements available plays)
export const startGame = async (gameName: string) => {
  const rawToken = SecureStorage.getCustomerToken();
  if (!rawToken) throw new Error('Customer not authenticated - please sign in again');
  try {
    const response = await axiosInstance.post('/public/games/start', { gameName }, {
      headers: { Authorization: withBearer(rawToken), 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error starting game:', error);
    throw error;
  }
};
